import { DropdownItem } from '@/main/store/@types/dropdown.d';
import _ from 'lodash';
import hash from 'object-hash';
import { columnsToCamelCase, Row, Table, cellNames } from '@/main/utils/table';
import { editStoreBuilder } from '@/main/store/pages/edit/builder';
import { commitAction, updateEditAction, updateEditActionNoLoading } from '@/main/store/actionsFunctions';
import { Status } from '@/main/store/@types/page';
import { UPDATE_BASIC_EDIT } from '@/main/store/listEditTypes';
import { dataMapper } from '@/main/utils/dataMapper';
import { objectToCamelCaseShallow } from '@/main/utils/stringHelper';
import dayjs from 'dayjs';

import { editActions, editMutations } from './types';
import {
	NAME as name,
	DEPENDENT_TABLES as depTableNames,
	CRUMB_LABEL_EDIT,
	SHODA_KEY,
	PREDCHUDCI_KEY,
	POHYB_MESICNI,
	POHYB_AUTOPRT, POHYB_SKLAD, REFERENCE,
	PART_PRICING_LOG,
	WEIGHTED_AVG,
} from '../constants';
import { apiProvider } from '../api';

const DISPONIBILITA_SKLAD_KEY = 'DisponibilitaSklad';
const DISPONIBILITA_ZAKAZKA_KEY = 'DisponibilitaZakazka';

export type VyrobekndOznDropDownItem = DropdownItem & { DodavatelId: number, DodavatelIdText: string, Value: string };

export const store = editStoreBuilder({
	name,
	apiProvider,
	crumbLabel: CRUMB_LABEL_EDIT,
	dropdownList: {
		DphSzbOzn: [],
		SkladOzn: [],
		VyrobekndOzn: [] as VyrobekndOznDropDownItem[],
		ZbozidruhOzn: [],
		SkladMjId: [],
		RabatskslevatypPzn: [],
		VydejMjId: [],
		OdhcenaPzn: [],
		PretacPzn: [],
		VybehPzn: [],
		AdjustmentReasons: [],
		OrderType: [],
		StorageLocations: [] as DropdownItem[],
	},
	edit: {
		AphlasPrp: null as boolean | null,
		BalicJdn: null as number | null,
		BatchUpdPrp: null as boolean | null,
		CreateStatus: null as number | null,
		DodavatelId: null as number | null,
		DodavatelIdText: null as string | null,
		GstGroupCode: null as string | null,
		DphSzbOzn: null as number | null,
		EconomyPrp: null as boolean | null,
		EditDat: null as Date | null,
		EditPracId: null as number | null,
		FumsKor: null as number | null,
		InvPnc: null as number | null,
		InvPoslNc: null as number | null,
		InvPoslpohyb: null as Date | null,
		InvStav: null as number | null,
		Kdc: null as number | null,
		MarketKod: null as string | null,
		Memo: null as string | null,
		MimoKusPrp: null as boolean | null,
		Nc: null as number | null,
		NcGd: null as number | null,
		NcPred: null as number | null,
		NdakceantigenPrp: null as boolean | null,
		NdNr: null as string | null,
		OdhcenaPzn: null as number | null,
		Oznam: null as string | null,
		Pc: null as number | null,
		PcPred: null as number | null,
		Pnc: null as number | null,
		PncKorekce: null as number | null,
		PoslpohybDat: null as Date | null,
		PoslprijemDat: null as Date | null,
		Poznamka2Dd: null as string | null,
		PretacPzn: null as number | null,
		PrijemkaId: null as number | null,
		PrijemkaNr: null as number | null,
		PrijemkaPfx: null as string | null,
		PseudoNdPrp: null as boolean | null,
		RabatSk: null as string | null,
		RabatskslevatypPzn: null as number | null,
		RabatSkText: null as string | null,
		SkladmistoId: null as number | null,
		SkladmistoIdPopis: null as string | null,
		SkladmistoIdSkladmisto: null as string | null,
		SkladMjId: null as number | null,
		SkladOzn: null as number | null,
		Stav: null as number | null,
		StavMax: null as number | null,
		StavMin: null as number | null,
		Text: null as string | null,
		TextprepisPrp: null as boolean | null,
		TiskeanprijPrp: null as boolean | null,
		VstupDat: null as Date | null,
		VstupPracId: null as number | null,
		VybehPzn: null as number | null,
		VydejkandTxt: null as string | null,
		VydejMjId: null as number | null,
		VydejMjPoc: null as number | null,
		VyrazenDat: null as Date | null,
		VyrobekndOzn: null as string | null,
		ZaznamId: null as number | null,
		ZbozidruhOzn: null as string | null,
		ZbozidruhOznText: null as string | null,
		IsStockAvailable: null as boolean | null,
		MaxHardReserveQty: null as number | null,
		ExpiryDat: null as Date | null,
		CustomerName: null as string | null,
		CustomerEmali: null as string | null,
		CustomerPhone: null as string | null,
		PartQty: null as string | null,
		AvailabilityDate: null as Date | null,
		CustomerId: null as number | null,
		partsEnquiryPrpActive: true,
		AdjustmentType: null as number | null,
		AdjustmentQty: null as number | null,
		AdjustmentReason: null as number | null,
		RequestRejectionCommt: null as string | null,
		labeling: null as string | null,
		SPInclGST: null as string | null,
		GSTPercentage: 0 as number,
		HsnCode: null as number | null,
		BinCode: null as string | null,
		MovingAverage: 0 as number,
		OrderType: null as string | null,
		WeightedAveragePrice: null as number | null,
		LocalRRP: null as number | null,
		StorageLocationList: [] as string[],
		IsObsolete: false as boolean | false,
		SupersededPartNumber: null as string | null,
		SupersededPartText: null as string | null,
		SupersededPartId: null as number | null,
	},
	localState: {
		DisponibilitaSklad: new Table() as Table,
		DisponibilitaZakazka: new Table() as Table,
		aktualniRok: true as boolean,
		vstupDatOd: dayjs().startOf('year').toDate(),
		vstupDatDo: dayjs().endOf('year').toDate(),
		disponsibilitaDotaz: 0 as number,
		disponsibilitaResult: 0 as number,
		disabledNdNrStep: false,
		identicalSupersededPart: false as boolean,
		isDifferentWarehouse: false as boolean,
	},
	depTableNames,
	depTableSettings: {
		[PART_PRICING_LOG]: {
			loadByEntityId: true,
			filter(state) {
				return {
					Brand: state.edit.VyrobekndOzn,
					NdNr: state.edit.NdNr,
					WareHouse: state.edit.SkladOzn
				};
			},
		},
		[SHODA_KEY]: {
			filter(state) {
				return {
					VyrobekndOzn: state.edit.VyrobekndOzn,
					NdNr: state.edit.NdNr,
					NaskladePrp: false,
				};
			},
		},
		[REFERENCE]: {
			filter(state) {
				return {
					NdskladId: state.edit.ZaznamId,
				};
			},
		},
		[PREDCHUDCI_KEY]: {
			filter(state) {
				return {
					VyrobekndOzn: state.edit.VyrobekndOzn,
					NdNr: state.edit.NdNr,
					NaskladePrp: false,
				};
			},
		},
		[POHYB_AUTOPRT]: {
			filter(state) {
				return {
					NdSkladId: state.edit.ZaznamId,
					DatumOd: state.localState.vstupDatOd,
					DatumDo: state.localState.vstupDatDo,
				};
			},
		},
		[POHYB_SKLAD]: {
			filter(state) {
				return {
					NdSkladId: state.edit.ZaznamId,
					DatumOd: state.localState.vstupDatOd,
					DatumDo: state.localState.vstupDatDo,
				};
			},
		},
		[POHYB_MESICNI]: {
			filter(state) {
				return {
					NdSkladId: state.edit.ZaznamId,
					DatumOd: state.localState.vstupDatOd,
					DatumDo: state.localState.vstupDatDo,
				};
			},
		},
		[WEIGHTED_AVG]: {
			loadByEntityId: true,
			filter(state) {
				return {
					Brand: state.edit.VyrobekndOzn,
					NdNr: state.edit.NdNr,
					WareHouse: state.edit.SkladOzn,
				};
			},
		},
	},
	extraActions: {
		[editActions.CHECK_IF_ND_EXISTS]: ({ state, commit, dispatch }) => updateEditActionNoLoading(
			state, commit, dispatch,
			apiProvider.getNdSklad({
				vyrobekndOzn: state.edit.VyrobekndOzn!,
				ndNr: state.edit.NdNr!,
				skladOzn: state.edit.SkladOzn!,
			}),
		),
		[editActions.DISPONIBILITA_SKLAD_TABLE_SCHEMA]: ({ commit, dispatch }) => commitAction(
			commit, dispatch,
			apiProvider.disponibilitaSkladSchema(),
			DISPONIBILITA_SKLAD_KEY, editMutations.DISPONIBILITA_SKLAD_TABLE_SCHEMA_MUTATION,
		),
		async [editActions.DISPONIBILITA_SKLAD_TABLE_DATA]({ commit, dispatch }, { filter }) {
			await commitAction(
				commit, dispatch,
				apiProvider.disponibilitaSkladRows(filter),
				DISPONIBILITA_SKLAD_KEY, editMutations.DISPONIBILITA_SKLAD_TABLE_DATA_MUTATION,
			);
		},
		[editActions.DISPONIBILITA_ZAKAZKA_TABLE_SCHEMA]: ({ commit, dispatch }) => commitAction(
			commit, dispatch,
			apiProvider.disponibilitaZakazkaSchema(),
			DISPONIBILITA_ZAKAZKA_KEY, editMutations.DISPONIBILITA_ZAKAZKA_TABLE_SCHEMA_MUTATION,
		),
		[editActions.DISPONIBILITA_ZAKAZKA_TABLE_DATA]: ({ commit, dispatch }, filter) => commitAction(
			commit, dispatch,
			apiProvider.disponibilitaZakazkaRows(filter),
			DISPONIBILITA_ZAKAZKA_KEY, editMutations.DISPONIBILITA_ZAKAZKA_TABLE_DATA_MUTATION,
		),
		[editActions.KOREKCE_STAVU]: ({ state, commit, dispatch }, { korekceData }) => updateEditAction(
			state, commit, dispatch,
			apiProvider.korekceStavu(korekceData),
		),
		[editActions.SPAREPART]: ({ state, commit, dispatch }, { sparePart }) => updateEditAction(
			state, commit, dispatch,
			apiProvider.saveSparePartReminders(sparePart),
		),
		[editActions.SUPERSEDED_PART_MAPPER]({ commit, dispatch }, { data }) {
			const dataForMutation = {
				NdNr: data.ndnr,
				Text: data.text,
				VyrobekndOzn: null as string | null,
				RecordId: data.value,
				Warehouse: data.Warehouse,
			};
			commit(editMutations.SUPERSEDED_PART_MAPPER, { data: dataForMutation });
		},
	},
	extraGetters: {},
	extraMutations: {
		[UPDATE_BASIC_EDIT](state, { data }) {
			data.DropDownList && (state.dropdownList = { ...(state.dropdownList ?? {}), ...data.DropDownList });
			delete data.DropDownList;
			data.Operations && (state.operations = data.Operations);
			delete data.Operations;
			if (data.ValidationRules) {
				if (!_.isEmpty(data.ValidationRules)) {
					state.validationRules = data.ValidationRules;
				}
				delete data.ValidationRules;
			}
			if (data.InvPoslpohyb) {
				const dateVal = new Date(data.InvPoslpohyb);
				if (!Number.isNaN(<any>dateVal)) {
					data.InvPoslpohyb = dateVal;
				}
			}
			dataMapper(state.edit, data);
			state.editHash = hash(state.edit);
			state.status = Status.ok;
		},
		[editMutations.DISPONIBILITA_SKLAD_TABLE_SCHEMA_MUTATION](state, { data }) {
			state.localState.DisponibilitaSklad.columns = columnsToCamelCase(data.Columns);
			state.localState.DisponibilitaSklad.operations = data.Operations;
			state.localState.DisponibilitaSklad.hasIdColumn = data.HasIdColumn;
		},
		[editMutations.DISPONIBILITA_SKLAD_TABLE_DATA_MUTATION](state, { data }) {
			state.localState.DisponibilitaSklad.rows = data.map((row: Row) => objectToCamelCaseShallow(row));
		},
		[editMutations.DISPONIBILITA_ZAKAZKA_TABLE_SCHEMA_MUTATION](state, { data }) {
			state.localState.DisponibilitaZakazka.columns = columnsToCamelCase(data.Columns);
			state.localState.DisponibilitaZakazka.operations = data.Operations;
			state.localState.DisponibilitaZakazka.hasIdColumn = data.HasIdColumn;
			state.localState.DisponibilitaZakazka.cellNames = cellNames(state.localState.DisponibilitaZakazka);
		},
		[editMutations.DISPONIBILITA_ZAKAZKA_TABLE_DATA_MUTATION](state, { data }) {
			state.localState.DisponibilitaZakazka.rows = data.map((row: Row) => objectToCamelCaseShallow(row));
		},
		[editMutations.ND_SKLAD_DODAVATEL_SELECT_MAPPER](state, { data }) {
			state.edit.DodavatelId = data.value;
			state.edit.DodavatelIdText = data.text;
		},
		[editMutations.ND_SKLAD_ZBOZI_DRUH_SELECT_MAPPER](state, { data }) {
			state.edit.ZbozidruhOzn = data.value;
			state.edit.ZbozidruhOznText = data.text;
		},
		[editMutations.ND_SKLAD_MISTO_SELECT_MAPPER](state, { data }) {
			state.edit.SkladmistoId = data.id;
			state.edit.SkladmistoIdSkladmisto = data.value;
			state.edit.SkladmistoIdPopis = data.text;
		},
		[editMutations.ND_SKLAD_RABAT_SK_SELECT_MAPPER](state, { data }) {
			state.edit.RabatSk = data.value;
			state.edit.RabatSkText = data.text;
		},
		[editMutations.ND_SKLAD_CREATE_KONTROLA_ND_MAPPER](state, { data }) {
			state.edit.BalicJdn = data.BalicJdn;
			state.edit.DphSzbOzn = data.DphSzbOzn;
			state.edit.EconomyPrp = data.EconomyPrp;
			state.edit.Kdc = data.PcGd;
			state.edit.MarketKod = data.MarketKod;
			state.edit.NcGd = data.PcGdRyo;
			state.edit.NdNr = data.NdNr;
			state.edit.OdhcenaPzn = data.OdhcenaPzn;
			state.edit.Pc = data.CenaGdMod;
			state.edit.PretacPzn = data.PretacPzn;
			state.edit.PseudoNdPrp = data.PseudoNdPrp;
			state.edit.RabatSk = data.RabatSk;
			state.edit.RabatSkText = data.RabatSkText;
			state.edit.Text = data.Text;
			state.edit.VybehPzn = data.VybehPzn;
			state.edit.VyrobekndOzn = data.VyrobekndOzn;
			state.edit.ZbozidruhOzn = data.ZbozidruhOzn;
			state.edit.ZbozidruhOznText = data.ZbozidruhOznText;
			state.edit.GSTPercentage = data.GSTPercentage;
			state.edit.HsnCode = data.HsnCode;
			state.edit.SkladMjId = data.BalicJdn;
			state.edit.VydejMjId = data.BalicJdn;
		},
		[editMutations.CUSTOMER_POJISTOVNA_FILTER_SELECT_MAPPER](state, { data }) {
			state.edit.CustomerId = data.value;
			state.edit.CustomerName = data.text;
			state.edit.CustomerEmali = data.EMailAdresa;
			state.edit.CustomerPhone = data.TelmobNr;
		},
		[editMutations.SUPERSEDED_PART_MAPPER](state, { data }) {
			if (state.edit.NdNr === data.NdNr) {
				state.localState.identicalSupersededPart = true;
			} else if (data.Warehouse !== undefined && state.edit.SkladOzn !== data.Warehouse) {
				state.localState.isDifferentWarehouse = true;
			} else {
				state.edit.SupersededPartNumber = data.NdNr;
				state.edit.SupersededPartText = data.Text;
				state.edit.SupersededPartId = data.RecordId;
			}
		},
	},
	props: {
		filter: {},
		mode: null,
	},
});
